import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { mailKeys } from '@/hooks/useMail'
import { completeMailOAuth } from '@/services/api/mail.service'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { getErrorMessage } from '@/utils/error-parser'

/** Where Google / Microsoft send the browser back; hands the one-time code to the server. */
export function MailboxCallbackPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const ws = useWorkspaceStore((s) => s.currentWorkspaceId)
  // The code can be used once, and React StrictMode runs effects twice in development.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const code = params.get('code')
    const state = params.get('state')
    const denied = params.get('error_description') ?? params.get('error')
    if (denied || !code || !state) {
      toast.error(denied ?? 'The sign-in was cancelled')
      navigate('/settings?tab=mailboxes', { replace: true })
      return
    }
    completeMailOAuth(code, state)
      .then((connection) => {
        toast.success(`${connection.emailAddress} connected`)
        return queryClient.invalidateQueries({ queryKey: mailKeys.connections(ws) })
      })
      .catch((error) => toast.error(getErrorMessage(error, 'Could not connect the mailbox')))
      .finally(() => navigate('/settings?tab=mailboxes', { replace: true }))
  }, [params, navigate, queryClient, ws])

  return (
    <div className="flex items-center justify-center gap-2 p-12 text-neutral-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Connecting your mailbox…
    </div>
  )
}
